import { UntypedFormGroup } from '@angular/forms';

import { captureMessage } from '@sentry/angular';
import { assignSafe, isString } from '@summit/core';

export class ApiError {
  message = '';
  // First array = field, second array = error
  model: Record<string, string[]> = {};

  constructor(err?: any /*noUnusedParameters , request?: HttpRequest<any> */) {
    //console.log('[APIError] Processing :', err);
    if (err) {
      //console.log('[APIError] Processing : step 1');
      if (isString(err)) {
        //console.log('[APIError] Processing : err is string');
        this.message = err;
      } else if (isString(err.error)) {
        //console.log('[APIError] Processing : is string');
        this.message = err.error;
      } else if (err.error && (err.error.message || err.error.model)) {
        //console.log('[APIError] Processing : is api error');
        // Process as API Error Object
        assignSafe(this, err.error);
      } else if (err?.error?.errors?.data) {
        // Process as API Error Object
        //console.log('Processed as Complex Api Error Object:', err);
        this.message = `${err.error.title}: ${err.error.errors.data.join(', ')}`;
      } else if (err && (err.message || err.model) && !err.error) {
        // Process as API Error Object
        console.log('Processed as Api Error Object2:', err);
        assignSafe(this, err);
      } else if (err && (err.title || err.message) && (err.errors || err.error?.errors)) {
        // * Parse as Generic Validation Message
        //console.log('Processing issue as generic:', this, err);
        this.message = err.title ?? err.message;
        this.model = err.errors ?? err.error?.errors;
      } else if (err && err.status === 0) {
        // Handle network connectivity errors (status 0)
        this.message = 'Unable to connect to EBO Pro. Please check your internet connection and try again.';
        console.warn('[APIError] Network connectivity issue:', err);
      } else if (err && err.status) {
        captureMessage('Unknown Server Response: ' + this.message, {
          //level: Sentry.Severity.Error,
          extra: {
            response: err
          }
        });
        //console.error(`Received Error Code ${err.code} with Error:`, err);
        let response = `An error has occurred.\nPlease contact support@ebopro.com and send them the information below:\n`;
        response += 'Error Type: HttpError Response\n';
        response += `Error Code: ${err.status}\n`;
        response += `Error Message: ${err.message}\n`;
        response += `Error detail: ${err.error}\n`;
        window.alert(response);
      } else {
        this.model = (err ?? {}) as Record<string, string[]>;
      }
    }
    if (Array.isArray(this.model)) {
      const temp = this.model;
      this.model = {};
      temp.forEach((entry) => {
        const key = entry[0];
        const value = entry[1];
        if (key && value) this.model[key] = value;
      });
    }
    if (this.message.includes('Http') || this.message.includes('System Error')) {
      //console.log('Notifying Sentry of Error');
      captureMessage('Decoding API Error Failed: ' + this.message, {
        //level: Sentry.Severity.Error,
        extra: {
          response: err
        }
      });
    }
    //console.log('[APIError] Processed:', this);
  }

  static ProcessServerError(err: any, elementForm: UntypedFormGroup): ApiError {
    //console.log('[APIError] Processing :', err);
    const lclApiError = new ApiError(err);
    //console.log('[APIError] Processed:', lclApiError);

    // For network errors (status 0), don't try to process form field errors
    if (err && err.status === 0) {
      return lclApiError;
    }

    elementForm.markAllAsTouched();
    if (lclApiError.model) {
      //lclApiError.model = Object.assign(new Array<[string, string[]]>(), lclApiError.model);
      const formKeys = Object.keys(elementForm.controls);
      const generalError = new Array<[string, string[]]>();
      let displayedErrors = new Array<string>();
      const errorModel = lclApiError.model;
      //console.log(`Processing Error Model to Form Items `, lclApiError.model, formKeys);
      //console.log(`Processing Error Model to Form Items `, Object.keys(lclApiError.model));
      const errorItems = Object.keys(errorModel);
      errorItems.forEach((key) => {
        const keyErrors = errorModel[key];
        if (key && keyErrors && keyErrors.length > 0) {
          const correctedKey = key.replace(/\[\d*\]/g, '');
          const altKey = correctedKey.charAt(0).toLowerCase() + correctedKey.slice(1);
          //console.log(`Looking to update Field: ${correctedKey} or ${altKey} in Error`, lclApiError);
          if (formKeys.indexOf(correctedKey) !== -1) {
            // NO White space
            //console.log(`Found: ${correctedKey} in Error`, lclApiError);
            const control = elementForm.get(correctedKey);
            control?.setErrors({ apiError: true });
            displayedErrors = displayedErrors.concat(keyErrors);
          } else if (formKeys.indexOf(altKey) !== -1) {
            // NO whitespace and 1st char is lowercase
            //console.log(`Found: ${altKey} in Error`, lclApiError);
            errorModel[altKey] = keyErrors;
            delete errorModel[key];
            const lclControl = elementForm.get(altKey);
            lclControl?.setErrors({ ...(lclControl?.errors ?? {}), ...{ apiError: true } });
            //console.log(`Found: ${altKey} in Error ${lclControl.errors}`, lclControl.errors);
            displayedErrors = displayedErrors.concat(keyErrors);
          } else if (formKeys.indexOf(correctedKey.toLowerCase()) !== -1) {
            // * No whitespace and all is lowercase
            //console.log(`Found: ${altKey} in Error`, lclApiError);
            const control = elementForm.get(correctedKey.toLowerCase());
            control?.setErrors({ apiError: true });
            displayedErrors = displayedErrors.concat(keyErrors);
          } else {
            //console.log(`Didn't find: ${key} in Error`, lclApiError);
            if (keyErrors) generalError.push([key, keyErrors]);
          }
        }
      });
      let generalErrorString = '';
      generalError.forEach((errorArray) => {
        if (Array.isArray(errorArray[1])) {
          errorArray[1].forEach((error, _index) => {
            if (displayedErrors.indexOf(error) < 0) {
              generalErrorString += `\n     ${errorArray[0]} - ${error}`;
            }
          });
        } else {
          //TODO
        }
      });
      if (generalErrorString.length > 0) {
        if (lclApiError.message) {
          lclApiError.message += '\n';
        }
        lclApiError.message += 'System Error: Please report to support@ebopro.com:';
        lclApiError.message += generalErrorString;
      }
    }
    //console.log('[APIError] Processed:', lclApiError, elementForm);
    return lclApiError;
  }
}
